// =============================================================================
// Copyright   : 2025 semantics GmbH
// Date        : $Date: 2025-11-01 16:43:09 +0100 (Sat, 01 Nov 2025) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 157706 $
// Purpose     : access restriction dashboard panels
// =============================================================================
// ---Third-party imports
import React from 'react'

// ---Framework imports
import log from 'uix/logging'
import {useApi, useState} from 'uix/hooks'
import {DashSegment} from 'uix/components/grid'
import {XMenu} from 'uix/components'
import {useColumnsForNode} from 'uix/db/hooks'
import {PropertyTable} from 'uix/db/proplist'
// ---Local imports

// ---Exports
const ttStyle = {
  fontFamily: "monospace, Menlo, Consolas, 'Courier New', monospace",
  fontSize: '0.9em',
  backgroundColor: '#f5f5f5', // light gray bg
  color: '#d6336c', // subtle accent (pinkish, tweak as needed)
  padding: '0.15em 0.4em',
  borderRadius: '4px',
  border: '1px solid #e1e1e1',
  whiteSpace: 'nowrap',
}

const TT = ({children}) => <tt style={ttStyle}>{children}</tt>

const RestrictionPanel = ({node, i18n, dconfig}) => {
  let [showInfo, setShowInfo] = useState(false)
  let [test_ip, set_test_ip] = useState('')
  let {data} = useApi(
    'restriction/evaluateGuestUserAccess',
    {otid: node.otid, test_ip},
    {condition: showInfo}
  )
  let guestUserAccess = data?.guestUserAccess || {}
  let {
    user_groups = [],
    user_ip,
    allowed = '',
    reason = '',
    restriction_name,
    restriction_groups = [],
    restriction_networks = [],
  } = guestUserAccess
  let groups = user_groups
    .filter(g => g != 'guest')
    .map((g, i) => (
      <span key={i}>
        <TT>{g}</TT>{' '}
      </span>
    ))
  if (!groups.length) {
    groups = <TT>[no groups]</TT>
  }
  let networks = restriction_networks.map((n, i) => (
    <span key={i}>
      <TT>{n}</TT>{' '}
    </span>
  ))
  if (restriction_networks.length == 0) {
    networks = <TT>[no networks configured]</TT>
  }
  if (restriction_groups.includes('*')) {
    // custom public group
    networks = <TT>*</TT>
  }

  let rgroups = restriction_groups
    .filter(g => g != '*')
    .map((g, i) => (
      <span key={i}>
        <TT>{g}</TT>{' '}
      </span>
    ))
  rgroups = rgroups.length ? <>that are configured for IP-groups {rgroups}</> : null

  let infoBlock = data ? (
    <>
      <div>
        Guest-User <TT>{user_ip}</TT> has IP-groups {groups}. This resolves to state{' '}
        <TT>{allowed.split(':').at(-1)}</TT> due to reason <TT>{reason.split(':').at(-1)}</TT>
      </div>
      <div style={{marginTop: '10px'}}>
        The active restriction <TT>{restriction_name}</TT> is accessible by the ip-networks{' '}
        {networks}
        {rgroups}
      </div>
    </>
  ) : null

  let columnList = useColumnsForNode({
    fields: [
      'restriction.value',
      'restriction.inherited',
      'restriction.inherited_otid',
      'oc.restrictionstate',
    ],
    nodeData: node,
    showEmpty: false,
  })
  let menu = (
    <XMenu>
      <XMenu.Input
        disabled={!showInfo}
        placeholder={user_ip}
        debounce={1000}
        onValueChange={set_test_ip}
        title="Change Test-IP"
      />
      <XMenu.Toggle icon="info" value={showInfo} set={setShowInfo} content="Guest" />
    </XMenu>
  )

  return (
    <DashSegment header={i18n.get('restriction')} menu={menu}>
      <PropertyTable columnList={columnList} />
      {infoBlock}
    </DashSegment>
  )
}

export const NodeRestrictions = {
  Comp: RestrictionPanel,
  //~ order: 99,
  align: 'right',
  enabled: ({dconfig}) => dconfig.accessLevels.on,
  datatypes: ['metadata'],
}
